import { Templates } from '@hellohair/types';
import { Box, MenuItem } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AddRowButton,
  FieldGroup,
  NextQuestionSelect,
  RepeatableRow,
  StepField,
  StepSelect,
} from '../_shared/fields';
import { ModalList } from '../_shared/ModalList';
import { appendTo, removeAt, replaceAt } from '../_shared/stepData';
import { StepDialog, StepEditorProps } from '../_shared/StepDialog';
import { useStepDraft } from '../_shared/useStepDraft';

/** Icons a trust chip can use. An empty value keeps the older image variant. */
const BADGE_ICONS: Templates.BadgeIcon[] = ['clock', 'lock', 'shield', 'check'];

const Landing: FC<StepEditorProps> = ({ survey, index, onClose }) => {
  const { t } = useTranslation();
  const { draft, set, save, dirty } = useStepDraft<Templates.StepDataLanding>(survey, index);
  const badges = draft.badges ?? [];
  const advisors = draft.advisors ?? [];
  const modals = draft.modals ?? [];
  const status = draft.status ?? { label: '' };
  const product = draft.product ?? { imageURL: '', name: '' };

  return (
    <StepDialog
      survey={survey}
      index={index}
      onSave={save}
      onClose={onClose}
      dirty={dirty}
      edit={
        <>
          <FieldGroup title={t('editor.section.status')} hint={t('editor.hint.status')}>
            <StepField
              label={t('editor.field.statusLabel')}
              value={status.label}
              onChange={(value) => set('status', { ...status, label: value })}
            />
            <StepField
              label={t('editor.field.statusNote')}
              value={status.note}
              onChange={(value) => set('status', { ...status, note: value })}
            />
          </FieldGroup>

          <StepField
            label={t('editor.field.message')}
            value={draft.question}
            onChange={(value) => set('question', value)}
            multiline
          />
          <StepField
            label={t('editor.field.extra')}
            value={draft.extra}
            onChange={(value) => set('extra', value)}
            multiline
          />

          <FieldGroup title={t('editor.section.product')} hint={t('editor.hint.product')}>
            <StepField
              label={t('editor.field.productName')}
              value={product.name}
              onChange={(value) => set('product', { ...product, name: value })}
            />
            <StepField
              label={t('editor.field.imageUrl')}
              value={product.imageURL}
              onChange={(value) => set('product', { ...product, imageURL: value })}
            />
            <StepField
              label={t('editor.field.eyebrow')}
              value={product.eyebrow}
              onChange={(value) => set('product', { ...product, eyebrow: value })}
            />
            <StepField
              label={t('editor.field.productTag')}
              value={product.tag}
              onChange={(value) => set('product', { ...product, tag: value })}
            />
          </FieldGroup>

          <FieldGroup title={t('editor.section.advisors')} hint={t('editor.hint.advisors')}>
            {advisors.map((advisor, i: number) => (
              <RepeatableRow
                key={i}
                removeLabel={t('editor.action.removeAdvisor')}
                onRemove={() => set('advisors', removeAt(advisors, i))}
              >
                <Box sx={{ flexGrow: 1, minWidth: '160px' }}>
                  <StepField
                    label={t('editor.field.imageUrl')}
                    value={advisor.imageURL}
                    onChange={(value) => set('advisors', replaceAt(advisors, i, { imageURL: value }))}
                  />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: '140px' }}>
                  <StepField
                    label={t('editor.field.imageAlt')}
                    value={advisor.alt}
                    onChange={(value) => set('advisors', replaceAt(advisors, i, { alt: value }))}
                  />
                </Box>
              </RepeatableRow>
            ))}
            <AddRowButton
              label={t('editor.add.advisor')}
              onClick={() => set('advisors', appendTo(advisors, { imageURL: '', alt: '' }))}
            />
            <StepField
              label={t('editor.field.advisorsText')}
              value={draft.advisorsText}
              onChange={(value) => set('advisorsText', value)}
              multiline
            />
          </FieldGroup>

          <FieldGroup title={t('editor.section.badges')} hint={t('editor.hint.badges')}>
            {badges.map((badge, i: number) => (
              <RepeatableRow
                key={i}
                removeLabel={t('editor.action.removeBadge')}
                onRemove={() => set('badges', removeAt(badges, i))}
              >
                <Box sx={{ flexGrow: 1, minWidth: '140px' }}>
                  <StepField
                    label={t('editor.field.label')}
                    value={badge.label}
                    onChange={(value) => set('badges', replaceAt(badges, i, { label: value }))}
                  />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: '140px' }}>
                  <StepSelect
                    label={t('editor.field.icon')}
                    value={badge.icon ?? ''}
                    onChange={(value) => set('badges', replaceAt(badges, i, { icon: value }))}
                  >
                    <MenuItem value="">{t('editor.icon.none')}</MenuItem>
                    {BADGE_ICONS.map((icon) => (
                      <MenuItem key={icon} value={icon}>
                        {t(`editor.icon.${icon}`)}
                      </MenuItem>
                    ))}
                  </StepSelect>
                </Box>
                {!badge.icon && (
                  <Box sx={{ flexGrow: 1, minWidth: '160px' }}>
                    <StepField
                      label={t('editor.field.imageUrl')}
                      value={badge.imageURL}
                      onChange={(value) => set('badges', replaceAt(badges, i, { imageURL: value }))}
                    />
                  </Box>
                )}
              </RepeatableRow>
            ))}
            <AddRowButton
              label={t('editor.add.badge')}
              onClick={() => set('badges', appendTo(badges, { label: '', icon: 'check' }))}
            />
          </FieldGroup>

          <ModalList modals={modals} onChange={(next) => set('modals', next)} />

          <StepField
            label={t('editor.field.consent')}
            value={draft.consent}
            onChange={(value) => set('consent', value)}
            multiline
          />
          <StepField
            label={t('editor.field.footnote')}
            value={draft.footnote}
            onChange={(value) => set('footnote', value)}
            multiline
          />
        </>
      }
      logic={
        <>
          <NextQuestionSelect
            steps={survey.steps}
            value={draft.followerId}
            onChange={(followerId) => set('followerId', followerId)}
          />
          <StepField
            label={t('editor.field.exitLink')}
            value={draft.exitLink}
            onChange={(value) => set('exitLink', value)}
          />
        </>
      }
      settings={
        <>
          <StepField
            label={t('editor.field.buttonLabel')}
            value={draft.next}
            onChange={(value) => set('next', value)}
          />
          <StepField label={t('editor.field.exitLabel')} value={draft.exit} onChange={(value) => set('exit', value)} />
        </>
      }
    />
  );
};

export default Landing;
